import { Component } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import classnames from "classnames"
import ErrorBoundary from "shared/components/error_boundary"

import { TimePeriods } from "./lib"

const SERVICE_NAME_REPLACER = "%SERVICE_NAME%"
const CATEGORY_NAME_REPLACER = "%CATEGORY_NAME%"
const WORKS_WITH_SERVICE_NAME_REPLACER = "%WORKS_WITH_SERVICE_NAME%"
const selectablePeriods = Object.freeze([
  { days: "last_07_days", description: "Last 7 days" },
  { days: "last_30_days", description: "Last 30 days" },
  { days: "last_90_days", description: "Last 90 days" },
])

export default function PeriodSelectionHOC({
  WrappedComponent,
  serviceMetricsFetcher = () => {},
  headerText,
  subHeaderText = null,
  fetchDailyComparisonData = true,
  limit,
  offset,
  topServicesLimit,
}) {
  class PeriodSelectionContainer extends Component {
    state = {
      timePeriod: this.props.timePeriod,
      data: this.props.data,
    }

    componentDidMount() {
      if (!this.renderableData(this.props.data)) {
        const {
          serviceModuleName,
          categoryName,
          worksWithChannelModuleName,
          appletSlug,
        } = this.props

        this.updateData(
          this.state.timePeriod,
          serviceModuleName,
          categoryName,
          worksWithChannelModuleName,
          appletSlug
        )
      }
    }

    renderableData(data) {
      return (
        (Array.isArray(data) && data.length) ||
        (typeof data === "object" && Object.keys(data).length)
      )
    }

    componentDidUpdate(prevProps) {
      // Allow a stand-alone context where there's no initial data
      if (
        this.props.appletSlug !== prevProps.appletSlug ||
        (this.props.categoryName !== prevProps.categoryName &&
          !this.renderableData(this.props.data)) ||
        (this.props.worksWithChannelModuleName !=
          prevProps.worksWithChannelModuleName &&
          !this.renderableData(this.props.data)) ||
        !this.state.data
      ) {
        this.updateData(
          this.state.timePeriod,
          this.props.serviceModuleName,
          this.props.categoryName,
          this.props.worksWithChannelModuleName,
          this.props.appletSlug
        )
      }
    }

    updateTimePeriod = e => {
      const {
        serviceModuleName,
        categoryName,
        worksWithChannelModuleName,
        appletSlug,
      } = this.props
      this.updateData(
        e.target.value,
        serviceModuleName,
        categoryName,
        worksWithChannelModuleName,
        appletSlug
      )
    }

    currentDate() {
      // moment uses self-mutating object to store and format dates, so create a new one for each use
      return moment(this.props.currentMetricDate)
    }

    updateData = (
      timePeriod,
      serviceModuleName,
      categoryName,
      worksWithChannelModuleName,
      appletSlug
    ) => {
      const timePeriodInDays = TimePeriods[timePeriod].days
      const currentMetricDate = this.currentDate().format("YYYY-MM-DD")
      const dailyMetricStartDate = this.currentDate()
        .subtract(timePeriodInDays * (fetchDailyComparisonData ? 2 : 1), "days")
        .format("YYYY-MM-DD")

      const comparisonMetricDate = this.currentDate()
        .subtract(timePeriodInDays, "days")
        .format("YYYY-MM-DD")

      serviceMetricsFetcher(
        serviceModuleName,
        categoryName,
        worksWithChannelModuleName,
        {
          dailyMetricStartDate,
          currentMetricDate,
          comparisonMetricDate,
          timePeriodInDays,
          timePeriodMetric: TimePeriods[timePeriod].metric,
          limit,
          offset,
          topServicesLimit,
          appletSlug,
        },
        this.props.mapper,
        this.props.query
      ).then(data =>
        this.setState({
          timePeriod,
          data,
        })
      )
    }

    render() {
      const { data, timePeriod } = this.state
      const headerText = this.props.headerText || headerText
      const subHeaderText = this.props.subHeaderText || subHeaderText
      const childProps = {
        ...this.props,
        data,
        timePeriodInDays: TimePeriods[timePeriod].days,
        timePeriod,
      }

      return (
        <ErrorBoundary placeholder="Sorry, we're missing some data">
          <div className="period-selector">
            <div className="feedback-page-header">
              <header
                className={classnames("with-actions", {
                  "page-header":
                    typeof subHeaderText === "string" &&
                    this.props.headerSeparator,
                })}
              >
                <div>
                  <h3>{headerText}</h3>
                  {subHeaderText && (
                    <span>
                      {subHeaderText
                        .replace(SERVICE_NAME_REPLACER, this.props.serviceName)
                        .replace(
                          CATEGORY_NAME_REPLACER,
                          this.props.categoryName
                        )
                        .replace(
                          WORKS_WITH_SERVICE_NAME_REPLACER,
                          this.props.worksWithChannelName
                        )}
                    </span>
                  )}
                </div>
                <div className="header-actions">
                  <select
                    onChange={this.updateTimePeriod}
                    defaultValue={this.props.timePeriod}
                  >
                    {this.props.selectablePeriods.map(period => (
                      <option key={period.days} value={period.days}>
                        {period.description}
                      </option>
                    ))}
                  </select>
                </div>
              </header>
              {this.renderableData(data) && (
                <WrappedComponent {...childProps} />
              )}
            </div>
          </div>
        </ErrorBoundary>
      )
    }
  }

  PeriodSelectionContainer.propTypes = {
    serviceModuleName: PropTypes.string,
    serviceName: PropTypes.string,
    timePeriod: PropTypes.string,
    headerText: PropTypes.string,
    subHeaderText: PropTypes.string,
    headerSeparator: PropTypes.bool,
    categoryName: PropTypes.string,
    worksWithChannelName: PropTypes.string,
    worksWithChannelModuleName: PropTypes.string,
    appletSlug: PropTypes.string,
    mapper: PropTypes.func,
    query: PropTypes.string,
    data: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.object),
      PropTypes.object,
    ]),
    currentMetricDate: PropTypes.string.isRequired,
    selectablePeriods: PropTypes.arrayOf(
      PropTypes.shape({
        days: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      })
    ),
  }

  PeriodSelectionContainer.defaultProps = {
    data: [],
    subHeaderText,
    headerText,
    headerSeparator: true,
    timePeriod: "last_30_days",
    selectablePeriods,
  }

  return PeriodSelectionContainer
}
